import React, { Suspense } from 'react';
import { WidgetFallback } from '@mummssoftware/common-ui';
import {
  withGlobalContext,
  withVisitsGlobalContext,
} from '@mummssoftware/global-context';
import { getLdapDn, getAgency } from '@mummssoftware/utils/auth';
import { BrowserRouter } from 'react-router-dom';
import { Pim, Scheduler } from '@mummssoftware/utils';
import { asyncForEach } from '@mummssoftware/utils/frequencies/frequency-utils';
import { DEFAULT_LOCALE } from './i18n';
import Providers from './Providers';
import { AppContext, AppContextProvider } from './context';
import { ScheduledVisitsContextProvider } from './calendar/context/ScheduledVisitsContext';
import { ScheduledVisitResponse } from './containers/ScheduledVisits/type';
import App from './containers/App';
import { CURRENT_USER, AGENCY } from './utils/constant';
import { formatAddEndpoint, formatVisitDate } from './utils/utils';
import defaults from './translations/en.json';

const translations: NonNullable<mumms.JSONObject> = defaults;

type ScheduledVisitProps = {
  online: boolean;
  keycloak?: Keycloak.KeycloakInstance | null;
  agency?: string | null;
  theme?: string | null;
  patientNumber?: string;
  locale?: keyof mumms.translations;
  /*  controls whether certain elements (i.e. the top bar) display */
  embedded?: boolean;
  /** DASHBOARD- smallWidget mode (NEEDED bc when dashboard in XS mode problem with width calc */
  smallWidget?: boolean;
  /* DASHBOARD key forcing React to render */
  key?: any;
  /* optional instance of pim, otherwise instantiates its own */
  pim?: Pim;
  /* if embedded title of the app or default Patient scores title */
  appTitle?: string;
  /* optional instance of scheduler, otherwise instantiates its own */
  scheduler?: Scheduler;
  /* pass currentUser if already retrieved to avoid excessive api calls */
  currentUser?: mumms.User;
  /* pass patient if already retrieved to avoid excessive api calls */
  patient?: mumms.patient;
  /* if the component is embedded onClick to toggle scores to full-size */
  onClick?: () => void;
  onRemove?: () => void;
  doNotShow?: boolean;
  view?: 'readonly' | 'editable';
  /* If watermark should display on top of the widget. */
  /* If readonly = true, the widget will not be interactive */
  watermark?: {
    show: boolean;
    readOnly: boolean;
  };
  /**
   * OP-33263: run without the CPVF / Visit Log widgets feeding the global
   * contexts (the cross patient compliance drill-down): the calendar loads
   * its own role groups and visits, and opens on `initialDate`.
   */
  standalone?: boolean;
  /** YYYY-MM-DD the calendar opens on (defaults to today). */
  initialDate?: string;
  /** YYYY-MM-DD end of the range the caller is looking at. */
  rangeEnd?: string;
};

interface ScheduledVisitState {
  context: AppContext | null;
  scheduledVisits: ScheduledVisitResponse;
  displayText: string;
}

const setPatientError = (res: Response, patientNumber?: string) => ({
  patientError: {
    status: res.status,
    statusText: res.statusText,
    patientNumber,
  },
});

const setPatient = (patient: mumms.patient | undefined) => patient || undefined;

const Content = React.memo(
  ({
    context,
    embedded,
    scheduledVisits,
    smallWidget,
  }: ScheduledVisitState & { embedded?: boolean; smallWidget?: boolean }) => {
    if (context === null) {
      return (
        <WidgetFallback
          embedded={embedded}
          appTitle={translations.scheduledVisits as string}
          smallWidget={smallWidget}
        />
      );
    }
    return (
      <AppContextProvider value={context}>
        <ScheduledVisitsContextProvider>
          <Suspense
            fallback={
              <WidgetFallback
                embedded={embedded}
                appTitle={context.appTitle as string}
                smallWidget={smallWidget}
              />
            }
          >
            <App
              currentUser={context.currentUser}
              embedded={embedded}
              online={context.online}
              patient={context.patient}
              logout={context.keycloak && context.keycloak.logout}
              view={context.view}
              scheduledVisits={scheduledVisits}
              onClick={context.onClick}
            />
          </Suspense>
        </ScheduledVisitsContextProvider>
      </AppContextProvider>
    );
  },
);

Content.displayName = 'MainContent';

class ScheduledVisit extends React.PureComponent<
  ScheduledVisitProps,
  ScheduledVisitState
> {
  pim?: Pim;

  scheduler?: Scheduler;

  online: boolean;

  agency: string | undefined;

  constructor(props: ScheduledVisitProps) {
    super(props);
    this.online = props.online;

    let { agency } = props;
    const { keycloak } = props;

    if (keycloak && this.online) {
      // if the agency has not been passed as props retrieve it
      // from keycloak, which is the most dependable way.
      // Non-admins do not pass an agency in the URL param
      // Agency is needed for keycloak configuration
      agency = agency || getAgency(getLdapDn(keycloak.tokenParsed));
      if (!agency) {
        // TODO: better error message
        throw new Error(`could not find agency`);
      }

      this.pim =
        props.pim ||
        new Pim({
          url: process.env.REACT_APP_PIM_BASEURL as string,
          keycloak,
          agency,
        });

      this.scheduler =
        props.scheduler ||
        new Scheduler({
          url: process.env.REACT_APP_PIM_BASEURL as string,
          keycloak,
          agency,
        });
    }

    this.state = {
      context: null,
      scheduledVisits: null,
      displayText: '',
    };
  }

  getInitialData = async () => {
    if (this.pim && this.scheduler && this.online) {
      const {
        currentUser: externalUser,
        patient,
        patientNumber = window.location.pathname.substring(1)?.split('/')[0],
      } = this.props;

      let patientState: any;
      let visitResponse: any;

      const [currentUser, patientResponse] = await Promise.all([
        // if provided a user via props, just pass it along
        !externalUser
          ? this.pim.getUserInfo(this.agency || '')
          : Promise.resolve(externalUser),
        !patient && patientNumber
          ? this.pim.getPatientHeader(patientNumber)
          : Promise.resolve(patient),
      ]);

      if (currentUser instanceof Response) {
        throw new Error(currentUser.statusText);
      } else {
        const agency = (this.props.agency ||
          currentUser.currentHospiceBin) as string;

        patientState =
          patientResponse instanceof Response
            ? {
                ...setPatientError(patientResponse, patientNumber),
              }
            : {
                patient: setPatient(patientResponse),
              };

        /**
         * Make a call to get all patients scheduled visits -
         * past, present, and future.
         */
        if (!(patientResponse instanceof Response) && patientResponse) {
          this.setState({
            context: {
              currentUser,
              agency,
              watermark: this.props.watermark,
              pim: this.pim,
              scheduler: this.scheduler,
              smallWidget: this.props.smallWidget,
              keycloak: this.props.keycloak,
              locale: this.props.locale || DEFAULT_LOCALE,
              view: this.props.view,
              online: this.online,
              appTitle: this.props.appTitle,
              ...patientState,
              scheduledVisits: visitResponse,
              onClick: this.props.onClick,
              onRemove: this.props.onRemove,
              doNotShow: this.props.doNotShow,
              standalone: this.props.standalone,
              initialDate: this.props.initialDate,
              rangeEnd: this.props.rangeEnd,
            },
            scheduledVisits: visitResponse,
          });
        }
      }
    } else {
      const currentUser = JSON.parse(
        localStorage.getItem(CURRENT_USER) as string,
      );
      this.setState({
        context: {
          currentUser,
          watermark: this.props.watermark,
          locale: this.props.locale || DEFAULT_LOCALE,
          online: this.online,
          agency: localStorage.getItem(AGENCY) as string,
          smallWidget: this.props.smallWidget,
          onClick: this.props.onClick,
        },
      });
    }
  };

  componentDidMount() {
    this.getInitialData();
  }

  render() {
    const { context, scheduledVisits, displayText } = this.state;
    const {
      embedded,
      theme,
      smallWidget,
      locale = DEFAULT_LOCALE,
    } = this.props;
    return (
      <Providers locale={locale} key={locale} theme={theme}>
        <BrowserRouter>
          <Content
            context={context}
            scheduledVisits={scheduledVisits}
            displayText={displayText}
            embedded={embedded}
            smallWidget={smallWidget}
          />
        </BrowserRouter>
      </Providers>
    );
  }
}

export default withGlobalContext(withVisitsGlobalContext(ScheduledVisit));
